// =============================================================================
// Stream Types for Streams Page
// Data source: PostgreSQL via GET /v4/streams (list)
// CRUD source: PostgreSQL via V4 stream endpoints
// =============================================================================

/**
 * V4 Stream from PostgreSQL (for table display)
 */
export interface Stream {
  id: string;
  mongoStreamId: string;
  name: string | null;
  probeSerial: string | null;
  description: string | null;
  mongoSensorId: string | null;
  mongoTransmitterId: string | null;
  mongoCalibrationId: string | null;
  organisationName: string | null;
  sensorName: string | null;
  calibrationDue: string | null;
  calibrationCertNumber: string | null;
  transmitterName: string | null;
  transmitterId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * Filters for querying streams list
 */
export interface StreamsFilters {
  search?: string;
  organisationName?: string;
  sortKey?: 'name' | 'sensorName' | 'organisationName' | 'probeSerial' | 'transmitterName' | 'calibrationDue' | 'calibrationCertNumber' | 'createdAt' | 'updatedAt';
  sortDirection?: 1 | -1;
  pageNumber?: number;
  pageLength?: number;
}

/**
 * API response for GET /v4/streams
 */
export interface StreamsResponse {
  streams: Stream[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

// =============================================================================
// Form Types (V4 PostgreSQL — legacy field shape for UI compatibility)
// =============================================================================

/**
 * Full stream document returned by GET /v4/streams/:id/edit.
 * The field names intentionally match the legacy stream form shape.
 */
export interface V2Stream {
  _id: string;
  name: string;
  organisation: string;
  sensor: string | null;
  transmitter: string | null;
  probe_serial: string | null;
  probe_type: number | null;
  ipso_id: string | null;
  description: string | null;
  value: number | null;
  offset: number;
  uncertainty: number | null;
  rounding_decimals: number | null;
  calibration_batch: string | null;
  onewire_mac: string | null;

  polynomial: {
    x1: number;
    x2: number;
    x3: number;
    x4?: number;
    x5?: number;
  };

  adc: {
    threshold: number | null;
    resolution: number | null;
    resistance: number | null;
  };

  steinharthart: {
    A: number | null;
    B: number | null;
    C: number | null;
  };

  filter: {
    min: number | null;
    max: number | null;
  };

  excluded_values: number[];

  calibration: {
    cert_number?: string;
    date?: string;
    cert?: string;
    due?: string;
    calibrationrecord?: string;
  } | null;

  created_at: string;
  updated_at: string;
}

/**
 * Dropdown option for sensors, transmitters, organisations
 */
export interface DropdownOption {
  _id: string;
  name: string;
  search_name?: string;
  dev_id?: string;
  xbee_SL?: string;
  customid?: string;
}

/**
 * Response from GET /v4/streams/:id/edit
 */
export interface StreamEditResponse {
  stream: V2Stream;
  options: {
    sensors: DropdownOption[];
    transmitters: DropdownOption[];
    organisations: DropdownOption[];
  };
}

/**
 * Response from GET /v4/streams/new-options
 */
export interface StreamNewOptionsResponse {
  sensors: DropdownOption[];
  transmitters: DropdownOption[];
  organisations: DropdownOption[];
}

/**
 * Input for creating/updating a stream via V4
 */
export interface StreamFormData {
  name: string;
  sensor: string | null;
  transmitter: string | null;
  organisation: string;
  probe_serial: string | null;
  description: string;
  offset: number;
  uncertainty: number | null;
  rounding_decimals: number | null;
  calibration_batch: string | null;
  polynomial: {
    x1: number;
    x2: number;
    x3: number;
    x4: number;
    x5: number;
  };
  adc: {
    threshold: number | null;
    resolution: number | null;
    resistance: number | null;
  };
  steinharthart: {
    A: number | null;
    B: number | null;
    C: number | null;
  };
  filter: {
    min: number | null;
    max: number | null;
  };
  excluded_values: number[];
}

/**
 * Preset sensor configuration
 */
export interface SensorPreset {
  label: string;
  color: string;
  adc: { threshold: number; resolution: number; resistance: number };
  steinharthart: { A: number; B: number; C: number };
}

export interface Organisation {
  _id: string;
  name: string;
  org_num?: number;
  enabled?: boolean;
}

export const SENSOR_PRESETS: Record<string, SensorPreset> = {
  TPT2: {
    label: 'TPT2 (REES Probe 2)',
    color: 'bg-red-500',
    adc: { threshold: 2.5, resolution: 4095, resistance: 4910 },
    steinharthart: { A: 0.00164, B: 0.00018, C: 3.15e-7 },
  },
  TPT3: {
    label: 'TPT3 (REES Probe 3)',
    color: 'bg-yellow-500',
    adc: { threshold: 2.5, resolution: 4095, resistance: 30100 },
    steinharthart: { A: 0.00159, B: 0.00021, C: 2.21e-7 },
  },
  TPT4: {
    label: 'TPT4 (REES Probe 4)',
    color: 'bg-blue-500',
    adc: { threshold: 5, resolution: 4095, resistance: 100000 },
    steinharthart: { A: 0.00155, B: 0.00023, C: 1.45e-7 },
  },
  XBEETMP36: {
    label: 'XBee TMP36',
    color: 'bg-purple-500',
    adc: { threshold: 0, resolution: 0, resistance: 0 },
    steinharthart: { A: 0, B: 0, C: 0 },
  },
  XBEEVCC: {
    label: 'XBee VCC',
    color: 'bg-purple-500',
    adc: { threshold: 0, resolution: 0, resistance: 0 },
    steinharthart: { A: 0, B: 0, C: 0 },
  },
};
